import { useAuth } from '@/hooks/useAuth';
import { useSubscription, type SubscriptionTier } from '@/hooks/useSubscription';
import { useAdmin } from '@/hooks/useAdmin';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { meetsMinTier, TIER_LABELS } from '@/lib/tierPermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, only require authentication (no subscription check) */
  authOnly?: boolean;
  /** Minimum subscription tier required to access this route */
  minTier?: SubscriptionTier;
}

export function ProtectedRoute({ children, authOnly = false, minTier = 'stylist' }: ProtectedRouteProps) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { hasPaidAccess, tier, loading: subLoading } = useSubscription();
  const { isAdmin } = useAdmin();
  const location = useLocation();

  // Bypass auth entirely in preview/visual-edit mode
  const isPreview = typeof window !== 'undefined' && (
    window.location.hostname.includes('id-preview--') ||
    window.location.hostname === 'localhost'
  );
  if (isPreview) return <>{children}</>;

  // Show loading while auth or subscription status is resolving
  if (authLoading || (isAuthenticated && !authOnly && subLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-foreground" />
      </div>
    );
  }

  // Not logged in → redirect to login page, preserving the intended destination
  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  // Admins always get through
  if (isAdmin) return <>{children}</>;

  // Auth-only routes don't need subscription
  if (authOnly) return <>{children}</>;

  // Logged in but no active subscription → redirect to pricing
  if (!hasPaidAccess) {
    return <Navigate to="/pricing" replace />;
  }

  // Has a subscription but tier is too low → redirect to pricing
  if (minTier && !meetsMinTier(tier, minTier)) {
    return <Navigate to="/pricing" replace />;
  }

  return <>{children}</>;
}
